"use client";

/**
 * Élément de navigation desktop du header (>= lg) : un `NavigationMenuItem`
 * dont le trigger (disclosure pur, sans `<Link>` imbriqué) ouvre une grille
 * 2 colonnes de liens vers les pages du pilier (Chauffeur / Location /
 * Partenaires).
 *
 * Extrait tel quel du `Header` pour supprimer la triplication : le markup et
 * les classes Tailwind sont identiques à l'original. Les `href` restent
 * canoniques FR et sont localisés au rendu via `localeNavHref`.
 */
import Link from "next/link";
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { type Locale } from "@/config/i18n";
import { localeNavHref } from "@/config/localeRoutes";
import { type NavServiceItem } from "@/components/layout/navDict";

type Props = {
  /**
   * Libellé déjà résolu dans la locale courante (ex. `t.triggerChauffeur[locale]`).
   * `string | undefined` car l'accès `I18nText[locale]` est optionnel pour `ar`
   * (déploiement incrémental) ; on transmet la valeur telle quelle, à l'identique
   * de l'usage inline d'origine (React ignore un enfant `undefined`).
   */
  label: string | undefined;
  /** Services du pilier (FR `href` canoniques + libellés i18n). */
  items: NavServiceItem[];
  /** Locale détectée du chemin courant (pour localiser les `href`/libellés). */
  locale: Locale;
  /** Contexte « location » → accents or au lieu du primaire. */
  isLocationRoute: boolean;
};

export function HeaderNavDropdown({
  label,
  items,
  locale,
  isLocationRoute,
}: Props) {
  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger
        className={`whitespace-nowrap font-semibold ${
          isLocationRoute
            ? "text-gray-200 data-[state=open]:text-gold-200"
            : "text-gray-200 data-[state=open]:text-primary"
        }`}
      >
        {label}
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <div className="grid w-[min(600px,calc(100vw-2rem))] gap-3 p-4 md:grid-cols-2">
          {items.map((service) => (
            <NavigationMenuLink
              key={service.href}
              asChild
              className={`block select-none space-y-1 rounded-md border border-transparent p-3 leading-none no-underline outline-none transition-colors hover:text-gray-900 focus:bg-transparent focus:text-gray-900 ${
                isLocationRoute
                  ? "hover:border-gold-200"
                  : "hover:border-primary"
              }`}
            >
              <Link href={localeNavHref(service.href, locale)}>
                <div className="text-sm font-semibold leading-none text-white">
                  {service.title[locale]}
                </div>
                <p className="line-clamp-2 text-sm leading-snug text-neutral-400">
                  {service.description[locale]}
                </p>
              </Link>
            </NavigationMenuLink>
          ))}
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
}
